import { PencilIcon } from '@heroicons/react/24/solid';
import { Button, ButtonProps } from '@praetorian-chariot/ui';
import { FeatureRadio } from '@praetorian-chariot/ui';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';

import { Loader } from '@/components/Loader';
import { Modal } from '@/components/Modal';
import { useScanLevel } from '@/hooks/useScanLevel';
import { AssetStatus } from '@/types';

const assetStatusMap = {
  'passive-enumeration': AssetStatus.ActivePassive,
  'active-enumeration': AssetStatus.ActiveLow,
  'vulnerability-discovery': AssetStatus.Active,
};

export const ScanLevelSetupForm = ({
  button,
  customerEmail,
}: {
  button?: {
    className?: string;
    label?: string;
    styleType?: ButtonProps['styleType'];
    size?: ButtonProps['size'];
    startIcon?: React.ReactNode;
  };
  customerEmail?: string;
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isScanLevelModalOpen = searchParams.get('scan-level') !== null;

  function handleClose() {
    setSearchParams(prev => {
      prev.delete('scan-level');
      return prev;
    });
  }

  function handleOpen() {
    setSearchParams(prev => {
      prev.set('scan-level', 'open');
      return prev;
    });
  }

  return (
    <>
      <Button
        onClick={handleOpen}
        label={'Scan Levels'}
        styleType="primary"
        size="sm"
        startIcon={<PencilIcon className="size-4" />}
        data-testid="scan-level-open-button"
        {...button}
      />
      <Modal
        title="Scan Levels"
        open={isScanLevelModalOpen}
        onClose={handleClose}
        footer={{
          text: 'Apply',
          form: 'scan-level-setup-form',
        }}
        data-testid="scan-level-modal"
      >
        <ScanLevelSetupFormComponent
          customerEmail={customerEmail}
          onSuccess={handleClose}
        />
      </Modal>
    </>
  );
};

export function ScanLevelSetupFormComponent({
  onSuccess,
  customerEmail,
}: {
  customerEmail?: string;
  onSuccess: () => void;
}) {
  const { updateScanLevel, currentScanLevel, isLoadingScanLevel } =
    useScanLevel({ email: customerEmail });
  const [selectedLevel, setSelectedLevel] = useState<AssetStatus | null>(
    currentScanLevel || null
  );

  // Add feature definitions
  const features = [
    {
      id: 'passive-enumeration',
      title: 'Passive Asset Enumeration',
      description:
        'Discovers assets using only publicly available information without sending packets to your infrastructure',
    },
    {
      id: 'active-enumeration',
      title: 'Active Asset Enumeration',
      description:
        'Performs direct network interaction with assets to discover services and configurations',
    },
    {
      id: 'vulnerability-discovery',
      title: 'Vulnerability Discovery',
      description:
        'Executes comprehensive vulnerability scanning using Nuclei to identify security vulnerabilities',
    },
  ];

  // Convert AssetStatus to feature ID
  const getFeatureId = (status: AssetStatus | null) => {
    return (
      Object.entries(assetStatusMap).find(
        ([_, value]) => value === status
      )?.[0] || null
    );
  };

  const handleScanLevelChange = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (selectedLevel !== null) {
      await updateScanLevel(selectedLevel);
      onSuccess();
    }
  };

  useEffect(() => {
    if (!isLoadingScanLevel) {
      setSelectedLevel(currentScanLevel || null);
    }
  }, [isLoadingScanLevel]);

  return (
    <Loader isLoading={isLoadingScanLevel} type="spinner" className="h-[374px]">
      <form
        id="scan-level-setup-form"
        className="flex flex-col gap-6"
        onSubmit={handleScanLevelChange}
        data-testid="scan-level-setup-form"
      >
        <p className="text-chariot-text-secondary text-sm">
          Select a default scan level that will be{' '}
          <strong className="font-bold italic">applied to all assets</strong>.
          This determines how Chariot works with all your assets..
        </p>

        <div className="space-y-3">
          {features.map(feature => (
            <FeatureRadio
              key={feature.id}
              {...feature}
              checked={getFeatureId(selectedLevel) === feature.id}
              onChange={() =>
                setSelectedLevel(
                  assetStatusMap[feature.id as keyof typeof assetStatusMap]
                )
              }
              data-testid={`scan-level-radio-${feature.id}`}
            />
          ))}
        </div>
      </form>
    </Loader>
  );
}
